import { cx } from "class-variance-authority";
import type { Key } from "react-aria-components";
import { ListBoxItem } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { AddIcon } from "@/assets/icons/ui/Add";
import type { AutocompleteItem } from "@/components/ui/inputs/Autocomplete/Autocomplete";

interface Props<TKey extends Key = Key> {
  item: AutocompleteItem<TKey>;
  isNewItem?: boolean;
  newItemTypeLabel?: string;
  index: number;
  renderCustomRow?: (item: AutocompleteItem<TKey>) => React.ReactNode;
}
export const autocompleteListItemStyles = {
  item: ({ isNewItem = false, isCustom = false }: { isNewItem?: boolean; isCustom?: boolean }) =>
    cx(
      // Essential styles for all items
      "group cursor-pointer outline-hidden",
      // Default item styles (only when not custom)
      !isCustom && [
        "text-labels-s font-weight-300",
        "flex items-center px-4 py-2",
        "bg-secondary-400",
        "hover:text-secondary-500",
        "hover:bg-secondary-50",
        "focus-visible:bg-secondary-200 focus-visible:text-secondary-50",
        "selected:bg-white selected:text-black",
        isNewItem ? "text-primary-500" : "text-white",
      ],
    ),
  icon: "h-4 w-4 pr-1 text-primary-500",
};

export const AutocompleteListItem = <TKey extends Key = Key>({
  item,
  isNewItem,
  newItemTypeLabel,
  index,
  renderCustomRow,
}: Props<TKey>) => {
  const { t } = useTranslation();

  const renderContent = () => {
    if (isNewItem) {
      return (
        <>
          <AddIcon className={cx(autocompleteListItemStyles.icon)} />
          {`${t("ui.autocomplete.createNewBtn", { query: item.label })} ${newItemTypeLabel || ""}`}
        </>
      );
    }

    if (renderCustomRow) {
      return renderCustomRow(item);
    }

    return item.label;
  };

  return (
    <ListBoxItem
      id={item.id}
      data-index={index}
      className={cx(autocompleteListItemStyles.item({ isNewItem, isCustom: !!renderCustomRow }))}
      textValue={item.label}
    >
      {renderContent()}
    </ListBoxItem>
  );
};
